/**
 * Bounded icon registry for composition icons.
 *
 * Claude sends icon names via MCP (e.g., "trending_up", "bar-chart-3").
 * Instead of importing the full lucide-react barrel (~5000 icons, ~3 MB),
 * we maintain a curated set of icons likely to be used in pages.
 *
 * If an icon isn't in the registry, CompIcon shows a fallback "?" glyph.
 * To add an icon: import it from 'lucide-react' and add to ICON_REGISTRY.
 */
import type { LucideIcon } from 'lucide-react';

// --- Chart / data visualization ---
import {
  BarChart3,
  LineChart,
  PieChart,
  AreaChart,
  ScatterChart,
  TrendingUp,
  TrendingDown,
  Activity,
  Gauge,
} from 'lucide-react';

// --- Navigation / layout ---
import {
  ArrowRight,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  ExternalLink,
  Link,
} from 'lucide-react';

// --- Status / alerts ---
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Info,
  Bell,
  Clock,
  Timer,
} from 'lucide-react';

// --- Objects / data ---
import {
  Database,
  Table2,
  FileText,
  File,
  Folder,
  FolderOpen,
  Package,
  Box,
  Layers,
  Grid,
} from 'lucide-react';

// --- Actions ---
import {
  Plus,
  Minus,
  X,
  Check,
  Search,
  Filter,
  Download,
  Upload,
  Share,
  Copy,
  Trash2,
  Pencil,
  Settings,
  RefreshCw,
} from 'lucide-react';

// --- People / communication ---
import { User, Users, Mail, MessageSquare, Globe, MapPin } from 'lucide-react';

// --- Finance / commerce ---
import { DollarSign, CreditCard, ShoppingCart, Briefcase, Building, Building2 } from 'lucide-react';

// --- Misc ---
import {
  Star,
  Heart,
  ThumbsUp,
  ThumbsDown,
  Zap,
  Target,
  Award,
  Flag,
  Bookmark,
  Tag,
  Hash,
  Calendar,
  Sun,
  Moon,
  Cloud,
  Lightbulb,
} from 'lucide-react';

export const ICON_REGISTRY: Record<string, LucideIcon> = {
  // Chart / data
  BarChart3,
  LineChart,
  PieChart,
  AreaChart,
  ScatterChart,
  TrendingUp,
  TrendingDown,
  Activity,
  Gauge,
  // Navigation
  ArrowRight,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  ExternalLink,
  Link,
  // Status
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Info,
  Bell,
  Clock,
  Timer,
  // Objects / data
  Database,
  Table2,
  FileText,
  File,
  Folder,
  FolderOpen,
  Package,
  Box,
  Layers,
  Grid,
  // Actions
  Plus,
  Minus,
  X,
  Check,
  Search,
  Filter,
  Download,
  Upload,
  Share,
  Copy,
  Trash2,
  Pencil,
  Settings,
  RefreshCw,
  // People / communication
  User,
  Users,
  Mail,
  MessageSquare,
  Globe,
  MapPin,
  // Finance
  DollarSign,
  CreditCard,
  ShoppingCart,
  Briefcase,
  Building,
  Building2,
  // Misc
  Star,
  Heart,
  ThumbsUp,
  ThumbsDown,
  Zap,
  Target,
  Award,
  Flag,
  Bookmark,
  Tag,
  Hash,
  Calendar,
  Sun,
  Moon,
  Cloud,
  Lightbulb,
};
